import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, CircularProgress,
  Alert, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

interface DocumentPdfDialogProps {
  open: boolean;
  onClose: () => void;
  fetchPdf: () => Promise<Blob>;
  title: string;
  filename: string;
}

/**
 * Shared dialog for viewing any document PDF inline.
 * Uses browser's native PDF rendering via iframe.
 * Responsive: full-screen on mobile, large modal on desktop.
 */
export const DocumentPdfDialog: React.FC<DocumentPdfDialogProps> = ({
  open,
  onClose,
  fetchPdf,
  title,
  filename,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // Fetch PDF when dialog opens
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears blob-URL state while revoking the object URL (external resource cleanup tied to previous state); no render-time equivalent
      setPdfBlobUrl((prevUrl) => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
      setError(null);
      return;
    }

    let currentBlobUrl: string | null = null;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);

      try {
        const blob = await fetchPdf();
        const url = URL.createObjectURL(blob);
        currentBlobUrl = url;
        setPdfBlobUrl(url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load PDF';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadPdf();

    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPdf is often an inline prop; including it would refetch the PDF on every parent render. Fetch is intentionally keyed on `open` only.
  }, [open]);

  const handleDownload = useCallback(() => {
    if (!pdfBlobUrl) return;

    const link = document.createElement('a');
    link.href = pdfBlobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfBlobUrl, filename]);

  const handleOpenInNewTab = useCallback(() => {
    if (!pdfBlobUrl) return;
    window.open(pdfBlobUrl, '_blank');
  }, [pdfBlobUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '90vh',
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="span" noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close dialog"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography color="text.secondary">Loading PDF...</Typography>
          </Box>
        )}

        {error && !loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 3,
            }}
          >
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
            <Typography variant="h6" color="error">
              Failed to load PDF
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => onClose()}>
              Close
            </Button>
          </Box>
        )}

        {pdfBlobUrl && !loading && !error && (
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: 'grey.100',
              ...theme.applyStyles('dark', { bgcolor: 'grey.800' }),
            })}
          >
            {isMobile && (
              <Alert
                severity="info"
                sx={{ m: 1, py: 0.5 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleOpenInNewTab}
                    startIcon={<OpenInNewIcon fontSize="small" />}
                  >
                    Open
                  </Button>
                }
              >
                For better viewing, open in a new tab
              </Alert>
            )}

            <Box
              component="iframe"
              src={`${pdfBlobUrl}#toolbar=1&navpanes=0`}
              title={title}
              sx={{
                flex: 1,
                width: '100%',
                border: 'none',
                bgcolor: 'background.paper',
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          gap: 1,
        }}
      >
        {(isMobile || isTablet) && pdfBlobUrl && (
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Open in New Tab
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!pdfBlobUrl || loading}
        >
          Download
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
